import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AccessTime, Close, EventAvailable, NotificationsActive } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetMyInterviewsQuery } from "state/api";
import {
  APP_DATA_TIMEZONE,
  formatClockParts,
  formatDisplayTime,
  formatYmdInTimeZone,
  zonedWallTimeToDate,
} from "utils/datetime";
import { scheduleProfileDisplay } from "utils/scheduleProfileDisplay";

const ALERT_WINDOW_MS = 10 * 60 * 1000; // notify from 10 minutes before
const ALERT_END_MS = 2 * 60 * 1000; // stop nagging 2 minutes after start
const NOTIFIED_KEY = "interviewAlertNotified_v1";

const loadNotified = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(NOTIFIED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

const saveNotified = (set) => {
  try {
    sessionStorage.setItem(NOTIFIED_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
};

const interviewKey = (inv) =>
  `${inv.scheduleId || ""}|${inv.profileName}|${inv.companyName}|${inv.fromTime}`;

const ensureNotificationPermission = async () => {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
};

/**
 * Live navbar clock in the member's display timezone, with upcoming interview
 * popover and 10-minute-before alerts (in-app dialog + desktop notification).
 */
const NavbarInterviewClock = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isNarrow = useMediaQuery("(max-width: 900px)");
  const timezone = useSelector((s) => s.global.timezone);
  const user = useSelector((s) => s.global.user);
  const role = user?.role;
  const tracksInterviews = ["caller", "assistant"].includes(role);

  const [now, setNow] = useState(() => new Date());
  const [anchorEl, setAnchorEl] = useState(null);
  const [alertInterview, setAlertInterview] = useState(null);
  const notifiedRef = useRef(loadNotified());

  const { data: myToday } = useGetMyInterviewsQuery(
    { dateFilter: "today" },
    {
      skip: !tracksInterviews,
      pollingInterval: tracksInterviews ? 60_000 : 0,
      refetchOnFocus: true,
    }
  );

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (tracksInterviews) ensureNotificationPermission();
  }, [tracksInterviews]);

  const clock = useMemo(() => formatClockParts(now, timezone), [now, timezone]);

  const todayYmdCentral = useMemo(() => formatYmdInTimeZone(now, APP_DATA_TIMEZONE), [now]);

  const upcoming = useMemo(() => {
    const list = Array.isArray(myToday?.interviews) ? myToday.interviews : [];
    return list
      .map((inv) => {
        const startAt = zonedWallTimeToDate(todayYmdCentral, inv.fromTime, APP_DATA_TIMEZONE);
        const endAt = zonedWallTimeToDate(todayYmdCentral, inv.toTime, APP_DATA_TIMEZONE);
        return { ...inv, startAt, endAt };
      })
      .filter((inv) => inv.startAt && (!inv.endAt || inv.endAt.getTime() >= now.getTime() - ALERT_END_MS))
      .sort((a, b) => a.startAt - b.startAt);
  }, [myToday, todayYmdCentral, now]);

  const nextInterview = upcoming.find((inv) => inv.startAt.getTime() > now.getTime() - ALERT_END_MS) || null;

  const fireDesktopNotification = useCallback((inv) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const title = "Interview starting soon";
    const body = `${inv.fromTime} · ${inv.companyName} (${scheduleProfileDisplay(inv.profileName)}) — join in ~10 minutes`;
    try {
      const n = new Notification(title, {
        body,
        tag: interviewKey(inv),
        renotify: true,
        requireInteraction: true,
      });
      n.onclick = () => {
        window.focus();
        navigate("/interviews");
        n.close();
      };
    } catch {
      /* ignore */
    }
  }, [navigate]);

  // 10-minute-before alert watcher
  useEffect(() => {
    if (!tracksInterviews || !upcoming.length) return;

    for (const inv of upcoming) {
      const msUntil = inv.startAt.getTime() - now.getTime();
      if (msUntil > ALERT_WINDOW_MS || msUntil < -ALERT_END_MS) continue;

      const key = interviewKey(inv);
      if (notifiedRef.current.has(key)) continue;

      notifiedRef.current.add(key);
      saveNotified(notifiedRef.current);
      setAlertInterview(inv);
      fireDesktopNotification(inv);
      break;
    }
  }, [tracksInterviews, upcoming, now, fireDesktopNotification]);

  const open = Boolean(anchorEl);
  const minutesUntilNext = nextInterview
    ? Math.round((nextInterview.startAt.getTime() - now.getTime()) / 60000)
    : null;
  const soon = minutesUntilNext != null && minutesUntilNext <= 10 && minutesUntilNext >= -2;

  const goToInterviews = () => {
    setAnchorEl(null);
    setAlertInterview(null);
    navigate("/interviews");
  };

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<AccessTime sx={{ fontSize: 16 }} />}
        title={`${clock.dateLabel} · ${clock.abbr} — click for today's interviews`}
        sx={{
          textTransform: "none",
          color: soon ? "#0369a1" : theme.palette.text.secondary,
          fontWeight: 700,
          fontSize: { xs: "0.68rem", sm: "0.75rem" },
          minWidth: "auto",
          px: { xs: 0.75, sm: 1 },
          py: 0.35,
          borderRadius: "10px",
          bgcolor: soon ? "rgba(14,165,233,0.14)" : theme.palette.action.hover,
          border: soon ? "1px solid rgba(14,165,233,0.45)" : `1px solid ${theme.palette.divider}`,
          fontVariantNumeric: "tabular-nums",
          "& .MuiButton-startIcon": { mr: 0.5 },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15 }}>
          <Typography component="span" sx={{ fontSize: "inherit", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
            {isNarrow ? clock.shortTimeLabel : clock.timeLabel}
            <Typography component="span" sx={{ ml: 0.5, fontSize: "0.65rem", fontWeight: 700, opacity: 0.75 }}>
              {clock.abbr}
            </Typography>
          </Typography>
          {!isNarrow && (
            <Typography component="span" sx={{ fontSize: "0.6rem", fontWeight: 600, opacity: 0.7 }}>
              {clock.dateLabel}
              {nextInterview ? ` · Next ${formatDisplayTime(nextInterview.startAt, { timeZone: timezone })}` : ""}
            </Typography>
          )}
        </Box>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 320, maxWidth: "92vw", borderRadius: "12px", mt: 0.75 } }}
      >
        <Box sx={{ px: 1.5, pt: 1.25, pb: 0.75, display: "flex", alignItems: "center", gap: 1 }}>
          <EventAvailable sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 800 }}>Today&apos;s interviews</Typography>
            <Typography sx={{ fontSize: "0.65rem", color: "text.secondary" }}>
              Times shown in {clock.abbr} · schedules stored in Central Time
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setAnchorEl(null)}>
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Divider />
        {!tracksInterviews ? (
          <Typography sx={{ px: 1.5, py: 2, fontSize: "0.75rem", color: "text.secondary" }}>
            Sign in as a caller to see and join today&apos;s interview slots.
          </Typography>
        ) : upcoming.length === 0 ? (
          <Typography sx={{ px: 1.5, py: 2, fontSize: "0.75rem", color: "text.secondary" }}>
            No upcoming interviews left for today.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 320, overflow: "auto" }}>
            {upcoming.map((inv) => {
              const mins = Math.round((inv.startAt.getTime() - now.getTime()) / 60000);
              const isLive = mins <= 0 && mins >= -2;
              const isSoon = mins > 0 && mins <= 10;
              return (
                <ListItemButton key={interviewKey(inv)} onClick={goToInterviews} sx={{ alignItems: "flex-start" }}>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
                        {formatDisplayTime(inv.startAt, { timeZone: timezone })}
                        {" – "}
                        {formatDisplayTime(inv.endAt || inv.startAt, { timeZone: timezone })}
                        {isLive ? " · Now" : isSoon ? ` · in ${mins}m` : ""}
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>
                        {inv.companyName} · {scheduleProfileDisplay(inv.profileName)} · {inv.type || "Interview"}
                      </Typography>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
        {tracksInterviews && (
          <>
            <Divider />
            <Box sx={{ p: 1 }}>
              <Button fullWidth size="small" variant="contained" onClick={goToInterviews} sx={{ textTransform: "none", fontWeight: 700 }}>
                Open interviews to join
              </Button>
            </Box>
          </>
        )}
      </Popover>

      <Dialog open={Boolean(alertInterview)} onClose={() => setAlertInterview(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800, fontSize: "1rem" }}>
          <NotificationsActive color="warning" />
          Interview in ~10 minutes
        </DialogTitle>
        <DialogContent>
          {alertInterview && (
            <Box>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, mb: 0.5 }}>
                {alertInterview.companyName}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                {scheduleProfileDisplay(alertInterview.profileName)} · {alertInterview.type || "Interview"}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", mt: 1.25, fontWeight: 700 }}>
                Starts {formatDisplayTime(alertInterview.startAt, { timeZone: timezone })} ({clock.abbr})
                {alertInterview.fromTime ? ` · schedule ${alertInterview.fromTime} CT` : ""}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 1 }}>
                Open Interviews to join and update your response.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setAlertInterview(null)} sx={{ textTransform: "none" }}>
            Dismiss
          </Button>
          <Button variant="contained" onClick={goToInterviews} sx={{ textTransform: "none", fontWeight: 700 }}>
            Join / open interviews
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NavbarInterviewClock;
